import { defineStore } from 'pinia'
import type {
    NetworkDataFromDetail,
    NetworkDataFromListing,
    Statistics,
    WarningMessage
} from '~~/interfaces/network'
import { sortWithNull } from '~~/app/utils/sort'

export const useNetworkStore = defineStore('network', () => {
    const baseUrl = tryUseNuxtApp()
        ? useRuntimeConfig().public.APPLICATION_URL
        : process.env.NUXT_PUBLIC_APPLICATION_URL

    const slug = ref('')
    const networkData = ref<NetworkDataFromDetail | null>(null)
    const networkStatistics = ref<Statistics | null>(null)
    const topTenNetworks = ref<Statistics[]>([])
    const rankedNetworks = ref<NetworkDataFromListing[]>([])
    const cachedDataNetworkDetailDate = ref('')
    const cachedDataNetworkStatisticsDate = ref('')
    const cachedDataNetworkRankedNetworksDate = ref('')
    const cachedDataNetworkTopTenNetworksDate = ref('')

    const id = computed<number>(() => networkData.value?.id || 0)
    const name = computed<string>(() => networkData.value?.name || '')

    const chargingZonesNumber = computed<number>(
        () => networkData.value?.totalPoolsCount || 0
    )

    const compatiblePrefix = computed<string>(() =>
        networkData.value?.isChargemapPassCompatible
            ? 'compatible'
            : 'not_compatible'
    )

    const isIonity = computed<boolean>(
        () => networkData.value?.name?.toLowerCase() === 'ionity'
    )

    const isTesla = computed<boolean>(
        () => networkData.value?.name?.toLowerCase().includes('tesla') || false
    )

    const globalNote = computed<number>(
        () => networkStatistics.value?.globalNoteAverage || 0
    )

    const ratingsCount = computed<number>(
        () => networkStatistics.value?.ratingsCount || 0
    )

    const materialNote = computed<number>(
        () => networkStatistics.value?.materialNoteAverage || 0
    )

    const priceNote = computed<number>(
        () => networkStatistics.value?.priceNoteAverage || 0
    )

    const locationNote = computed<number>(
        () => networkStatistics.value?.locationNoteAverage || 0
    )

    const securityNote = computed<number>(
        () => networkStatistics.value?.securityNoteAverage || 0
    )

    const checkinsCount = computed<number>(
        () => networkStatistics.value?.checkinsCount || 0
    )

    const isCompatible = computed<boolean>(
        () => networkData.value?.isChargemapPassCompatible || false
    )

    const warningMessage = computed<string>(() => {
        const localisedMessage = networkData.value?.warningMessages?.find(
            (message: WarningMessage) =>
                message.i18nLocale ===
                useLanguage() + '_' + useCountry().toUpperCase()
        )

        return localisedMessage?.message || ''
    })

    const defaultCenter = computed<[number, number]>(() => [
        networkData.value?.initialCenterLatitude || 0,
        networkData.value?.initialCenterLongitude || 0
    ])

    const getRatingFromCommunityFeedbacks = (
        network: NetworkDataFromListing
    ) => {
        const networkFoundInTopTen = topTenNetworks.value?.find(
            (networkInTopTen: Statistics) =>
                network.id === networkInTopTen.networkId
        )

        return networkFoundInTopTen?.globalNoteAverage ?? network.rating ?? 0
    }

    const fetchNetworkData = async () => {
        if (slug.value) {
            try {
                const [data, date] = await $fetch<
                    [NetworkDataFromDetail, string]
                >(`${baseUrl}/api/networks/detail`, {
                    query: {
                        slug: slug.value
                    }
                })
                networkData.value = data
                cachedDataNetworkDetailDate.value = date
            } catch (error: any) {
                if (error.status !== 404) {
                    useNuxtApp().$throwError(error, 'FetchNetworkData')
                }

                return showError({
                    statusCode: error.status,
                    statusMessage: error.statusText,
                    fatal: true
                })
            }
        }
    }

    const fetchRankedNetworks = async () => {
        if (topTenNetworks.value.length) {
            const networkIds = topTenNetworks.value?.map(
                (stat: Statistics) => stat.networkId
            )

            try {
                const [data, date] = await $fetch<
                    [NetworkDataFromListing[], string]
                >(`${baseUrl}/api/networks/rankedNetworks`, {
                    query: {
                        networkIds
                    }
                })
                rankedNetworks.value = data
                cachedDataNetworkRankedNetworksDate.value = date

                rankedNetworks.value.sort(
                    (
                        network1: NetworkDataFromListing,
                        network2: NetworkDataFromListing
                    ) =>
                        sortWithNull(
                            getRatingFromCommunityFeedbacks(network1),
                            getRatingFromCommunityFeedbacks(network2),
                            false
                        )
                )
            } catch (error: any) {
                if (error.status !== 404) {
                    useNuxtApp().$throwError(error, 'FetchRankedNetworks')
                }

                return showError({
                    statusCode: error.status,
                    statusMessage: error.statusText,
                    fatal: true
                })
            }
        }
    }

    const fetchTopTenNetworks = async () => {
        try {
            const [data, date] = await $fetch<[Statistics[], string]>(
                `${baseUrl}/api/networks/topTenNetworks`
            )
            topTenNetworks.value = data
            cachedDataNetworkTopTenNetworksDate.value = date
        } catch (error: any) {
            if (error.status !== 404) {
                useNuxtApp().$throwError(error, 'FetchTopTenNetworks')
            }

            return showError({
                statusCode: error.status,
                statusMessage: error.statusText,
                fatal: true
            })
        }
    }

    const fetchNetworkStatistics = async () => {
        if (id.value) {
            try {
                const [data, date] = await $fetch<[Statistics, string]>(
                    `${baseUrl}/api/networks/statistics`,
                    {
                        query: {
                            id: id.value
                        }
                    }
                )
                networkStatistics.value = data
                cachedDataNetworkStatisticsDate.value = date
            } catch (error: any) {
                if (error.status !== 404) {
                    useNuxtApp().$throwError(error, 'FetchNetworkStatistics')
                }

                return showError({
                    statusCode: error.status,
                    statusMessage: error.statusText,
                    fatal: true
                })
            }
        }
    }

    return {
        slug,
        networkData,
        networkStatistics,
        topTenNetworks,
        rankedNetworks,
        cachedDataNetworkDetailDate,
        cachedDataNetworkStatisticsDate,
        cachedDataNetworkRankedNetworksDate,
        cachedDataNetworkTopTenNetworksDate,
        id,
        name,
        chargingZonesNumber,
        compatiblePrefix,
        isIonity,
        isTesla,
        globalNote,
        ratingsCount,
        materialNote,
        priceNote,
        locationNote,
        securityNote,
        checkinsCount,
        isCompatible,
        warningMessage,
        defaultCenter,
        getRatingFromCommunityFeedbacks,
        fetchNetworkData,
        fetchRankedNetworks,
        fetchTopTenNetworks,
        fetchNetworkStatistics
    }
})

type NetworkStore = Omit<
    ReturnType<typeof useNetworkStore>,
    keyof ReturnType<typeof defineStore>
>

export default useNetworkStore

export type { NetworkStore }
